import type { GraphicsSettings, Quality } from '../hud';

export type PlayMode = 'fly' | 'walk' | 'drive';

export interface MenuCallbacks {
  onPlay: (mode: PlayMode) => void;
  onResume: () => void;
  onGraphics: (g: GraphicsSettings) => void;
  onAudio: (volume: number, muted: boolean) => void;
  onRegenerate: (seed: string) => void;
  onMouse: (sensitivity: number, invertY: boolean) => void;
  /** Current values, read whenever the menu opens. */
  state: () => { seed: string; graphics: GraphicsSettings; volume: number; muted: boolean; sensitivity: number; invertY: boolean };
}

const MODES: { mode: PlayMode; name: string; what: string; key: string }[] = [
  { mode: 'fly', name: 'Fly', what: 'A free camera over the city', key: '1' },
  { mode: 'walk', name: 'Walk', what: 'On foot, into the buildings', key: '2' },
  { mode: 'drive', name: 'Drive', what: 'A car through the traffic', key: '3' },
];

const CONTROLS = [
  ['Fly', 'WASD move · E/Space up · Q/C down · Shift fast · wheel speed'],
  ['Walk', 'WASD walk · Shift run · Space jump · F get into your car'],
  ['Drive', 'W/S throttle, brake, reverse · A/D steer · Space handbrake · F get out · R reset'],
  ['Any time', '1 fly · 2 walk · 3 drive · M sound · H hide HUD · Tab map · F3 stats · Esc menu'],
];

const WORDS = ['harbor', 'granite', 'maple', 'cobalt', 'ember', 'north', 'river', 'summit', 'amber', 'delta', 'lumen', 'quarry', 'willow', 'foundry', 'marina', 'sierra'];

/**
 * The title screen and the pause menu. The title shows over the live city (a slow orbit) with loading
 * progress, then the three ways in; the pause menu (Esc) offers the same choices plus Resume. Both
 * carry the settings: quality, sound and the city seed.
 */
export class Menu {
  readonly el: HTMLDivElement;
  mode: 'title' | 'pause' | 'hidden' = 'hidden';
  private readonly heading: HTMLDivElement;
  private readonly progress: HTMLDivElement;
  private readonly bar: HTMLDivElement;
  private readonly progressText: HTMLDivElement;
  private readonly resume: HTMLButtonElement;
  private readonly modeButtons: HTMLButtonElement[] = [];
  private readonly busy: HTMLDivElement;
  private readonly quality: HTMLButtonElement[] = [];
  private readonly soundOn: HTMLInputElement;
  private readonly volume: HTMLInputElement;
  private readonly seed: HTMLInputElement;
  private readonly sens: HTMLInputElement;
  private readonly invert: HTMLInputElement;

  constructor(
    parent: HTMLElement,
    private readonly cb: MenuCallbacks,
  ) {
    this.el = el('div', 'menu');
    const panel = el('div', 'panel', this.el);
    const logo = el('h1', 'logo', panel);
    logo.innerHTML = 'PERP<span>CITY</span>';
    this.heading = el('div', 'tagline', panel);
    this.progress = el('div', 'progress', panel);
    this.bar = el('div', 'bar', this.progress);
    this.progressText = el('div', 'progress-text', panel);

    this.resume = el('button', 'resume', panel);
    this.resume.textContent = 'Resume';
    this.resume.onclick = () => cb.onResume();
    const modes = el('div', 'modes', panel);
    for (const m of MODES) {
      const b = el('button', 'mode', modes);
      b.innerHTML = `<b>${m.name}</b><small>${m.what}</small><kbd>${m.key}</kbd>`;
      b.onclick = () => cb.onPlay(m.mode);
      b.dataset.mode = m.mode;
      this.modeButtons.push(b);
    }
    this.busy = el('div', 'busy', panel);

    const settings = el('div', 'settings', panel);
    // Quality: a segmented control; it sets shadows and AO with it (F3 has the finer switches).
    const qRow = row(settings, 'Quality');
    const seg = el('div', 'segmented', qRow);
    for (const q of ['low', 'mid', 'high'] as Quality[]) {
      const b = el('button', '', seg);
      b.textContent = q[0].toUpperCase() + q.slice(1);
      b.dataset.q = q;
      b.onclick = () => {
        cb.onGraphics({ quality: q, shadows: q !== 'low', ao: q !== 'low' });
        this.refresh();
      };
      this.quality.push(b);
    }
    const sRow = row(settings, 'Sound');
    const toggle = el('label', 'switch', sRow);
    this.soundOn = el('input', '', toggle);
    this.soundOn.type = 'checkbox';
    el('span', '', toggle);
    this.volume = el('input', 'volume', sRow);
    this.volume.type = 'range';
    this.volume.min = '0';
    this.volume.max = '1';
    this.volume.step = '0.01';
    const emitAudio = () => cb.onAudio(Number(this.volume.value), !this.soundOn.checked);
    this.soundOn.onchange = emitAudio;
    this.volume.oninput = () => {
      if (!this.soundOn.checked && Number(this.volume.value) > 0) this.soundOn.checked = true;
      emitAudio();
    };
    // Mouse: sensitivity on a log scale (0.0005 … 0.008 rad/px), and invert Y.
    const mRow = row(settings, 'Mouse');
    el('span', 'spacer', mRow);
    this.sens = el('input', 'volume', mRow);
    this.sens.type = 'range';
    this.sens.min = '0';
    this.sens.max = '1';
    this.sens.step = '0.01';
    this.sens.title = 'Look sensitivity';
    const invL = el('label', 'check', mRow);
    this.invert = el('input', '', invL);
    this.invert.type = 'checkbox';
    invL.append('Invert');
    const emitMouse = () => cb.onMouse(0.0005 * Math.pow(16, Number(this.sens.value)), this.invert.checked);
    this.sens.oninput = emitMouse;
    this.invert.onchange = emitMouse;
    const cRow = row(settings, 'City');
    this.seed = el('input', 'seed', cRow);
    this.seed.type = 'text';
    this.seed.spellcheck = false;
    this.seed.title = 'Any word or number: each seed is a different city';
    this.seed.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') cb.onRegenerate(this.seed.value);
    });
    const go = el('button', 'small', cRow);
    go.textContent = 'Build';
    go.onclick = () => cb.onRegenerate(this.seed.value);
    const dice = el('button', 'small', cRow);
    dice.textContent = 'Random';
    dice.title = 'A new random city';
    dice.onclick = () => {
      this.seed.value = `${WORDS[Math.floor(Math.random() * WORDS.length)]}-${Math.floor(Math.random() * 900 + 100)}`;
      cb.onRegenerate(this.seed.value);
    };

    const controls = el('details', 'controls', panel);
    el('summary', '', controls).textContent = 'Controls';
    const grid = el('div', 'grid', controls);
    for (const [k, v] of CONTROLS) {
      el('b', '', grid).textContent = k;
      el('span', '', grid).textContent = v;
    }
    const credits = el('div', 'credits', panel);
    credits.innerHTML = 'Textures ambientCG · models Quaternius · sounds Freesound &amp; Kenney: all CC0';
    // Clicks on the backdrop (outside the panel) resume a paused game.
    this.el.addEventListener('click', (e) => {
      if (e.target === this.el && this.mode === 'pause') cb.onResume();
    });
    parent.appendChild(this.el);
  }

  /** Re-read the settings (they can change elsewhere: keys, the F3 panel). */
  refresh(): void {
    const s = this.cb.state();
    for (const b of this.quality) b.classList.toggle('active', b.dataset.q === s.graphics.quality);
    this.soundOn.checked = !s.muted;
    this.volume.value = String(s.volume);
    this.sens.value = String(Math.log(s.sensitivity / 0.0005) / Math.log(16));
    this.invert.checked = s.invertY;
    if (document.activeElement !== this.seed) this.seed.value = s.seed;
  }

  showTitle(): void {
    this.mode = 'title';
    this.el.className = 'menu title';
    this.heading.textContent = 'A procedural city. Every seed is a new one.';
    this.setBusy('');
    this.refresh();
  }

  showPause(): void {
    this.mode = 'pause';
    this.el.className = 'menu pause';
    this.heading.textContent = 'Paused';
    this.setBusy('');
    this.refresh();
  }

  hide(): void {
    this.mode = 'hidden';
    this.el.className = 'menu hidden';
  }

  /** Loading progress on the title (0 … 1); the ways in unlock when ready. */
  setProgress(f: number, text: string, ready: boolean): void {
    this.bar.style.transform = `scaleX(${Math.max(0, Math.min(1, f)).toFixed(3)})`;
    if (this.progressText.textContent !== text) this.progressText.textContent = text;
    this.el.classList.toggle('ready', ready);
  }

  /** A way in was chosen and the player is being placed: show what's happening. */
  setBusy(text: string): void {
    this.busy.textContent = text;
    this.el.classList.toggle('is-busy', !!text);
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, parent?: HTMLElement): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  parent?.appendChild(e);
  return e;
}

function row(parent: HTMLElement, label: string): HTMLDivElement {
  const r = el('div', 'row', parent);
  el('span', 'label', r).textContent = label;
  return r;
}
